"use client"

import { useState } from "react"
import { yamlToProperties, propertiesToYaml } from "./converter"

// Example inputs
const yamlExampleInput = `# Server Configuration
server:
  port: 8080
  host: localhost
  timeout: 30

# Database Configuration
database:
  url: jdbc:mysql://localhost:3306/mydb
  username: admin
  password: secret
  pool:
    maxSize: 20
    minIdle: 5`

const propertiesExampleInput = `# Server Configuration
server.port=8080
server.host=localhost
server.timeout=30

# Database Configuration
database.url=jdbc:mysql://localhost:3306/mydb
database.username=admin
database.password=secret
database.pool.maxSize=20
database.pool.minIdle=5`

function YamlPropertiesConverter() {
  const [yamlInput, setYamlInput] = useState("")
  const [propertiesInput, setPropertiesInput] = useState("")
  const [yamlOutput, setYamlOutput] = useState("")
  const [propertiesOutput, setPropertiesOutput] = useState("")
  const [activeTab, setActiveTab] = useState("yaml-to-properties")
  const [error, setError] = useState(null)

  const handleYamlToProperties = () => {
    try {
      setError(null)
      const result = yamlToProperties(yamlInput)
      setPropertiesOutput(result)
    } catch (err) {
      setError(`Error converting YAML to Properties: ${err.message || String(err)}`)
      setPropertiesOutput("")
    }
  }

  const handlePropertiesToYaml = () => {
    try {
      setError(null)
      const result = propertiesToYaml(propertiesInput)
      setYamlOutput(result)
    } catch (err) {
      setError(`Error converting Properties to YAML: ${err.message || String(err)}`)
      setYamlOutput("")
    }
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
    alert("Copied to clipboard!")
  }

  const downloadFile = (content, fileType) => {
    const element = document.createElement("a")
    const file = new Blob([content], { type: "text/plain" })
    element.href = URL.createObjectURL(file)
    element.download = fileType === "yaml" ? "converted.yaml" : "converted.properties"
    document.body.appendChild(element)
    element.click()
    document.body.removeChild(element)
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Tabs */}
      <div className="flex border-b mb-6">
        <button
          className={`py-2 px-4 font-medium ${
            activeTab === "yaml-to-properties"
              ? "border-b-2 border-blue-500 text-blue-600"
              : "text-gray-500 hover:text-gray-700"
          }`}
          onClick={() => setActiveTab("yaml-to-properties")}
        >
          YAML → Properties
        </button>
        <button
          className={`py-2 px-4 font-medium ${
            activeTab === "properties-to-yaml"
              ? "border-b-2 border-blue-500 text-blue-600"
              : "text-gray-500 hover:text-gray-700"
          }`}
          onClick={() => setActiveTab("properties-to-yaml")}
        >
          Properties → YAML
        </button>
      </div>

      {/* YAML to Properties */}
      {activeTab === "yaml-to-properties" && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="space-y-6">
            {/* Input */}
            <div>
              <div className="flex justify-between mb-2">
                <label htmlFor="yaml-input" className="text-sm font-medium">
                  YAML Input
                </label>
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={() => setYamlInput(yamlExampleInput)}
                >
                  Load Example
                </button>
              </div>
              <textarea
                id="yaml-input"
                placeholder="Paste your YAML here..."
                className="w-full h-64 p-2 border border-gray-300 rounded-md font-mono text-sm"
                value={yamlInput}
                onChange={(e) => setYamlInput(e.target.value)}
              />
            </div>

            {/* Convert Button */}
            <div className="flex justify-center">
              <button
                onClick={handleYamlToProperties}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Convert to Properties
              </button>
            </div>

            {/* Error Message */}
            {error && <div className="p-4 bg-red-100 border border-red-200 text-red-700 rounded-md">{error}</div>}

            {/* Output */}
            <div>
              <div className="flex justify-between mb-2">
                <label htmlFor="properties-output" className="text-sm font-medium">
                  Properties Output
                </label>
                <div className="space-x-2">
                  <button
                    className="text-sm text-blue-600 hover:text-blue-800"
                    onClick={() => copyToClipboard(propertiesOutput)}
                    disabled={!propertiesOutput}
                  >
                    Copy
                  </button>
                  <button
                    className="text-sm text-blue-600 hover:text-blue-800"
                    onClick={() => downloadFile(propertiesOutput, "properties")}
                    disabled={!propertiesOutput}
                  >
                    Download
                  </button>
                </div>
              </div>
              <textarea
                id="properties-output"
                placeholder="Properties output will appear here..."
                className="w-full h-64 p-2 border border-gray-300 rounded-md font-mono text-sm"
                value={propertiesOutput}
                readOnly
              />
            </div>
          </div>
        </div>
      )}

      {/* Properties to YAML */}
      {activeTab === "properties-to-yaml" && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="space-y-6">
            {/* Input */}
            <div>
              <div className="flex justify-between mb-2">
                <label htmlFor="properties-input" className="text-sm font-medium">
                  Properties Input
                </label>
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={() => setPropertiesInput(propertiesExampleInput)}
                >
                  Load Example
                </button>
              </div>
              <textarea
                id="properties-input"
                placeholder="Paste your Properties here..."
                className="w-full h-64 p-2 border border-gray-300 rounded-md font-mono text-sm"
                value={propertiesInput}
                onChange={(e) => setPropertiesInput(e.target.value)}
              />
            </div>

            {/* Convert Button */}
            <div className="flex justify-center">
              <button
                onClick={handlePropertiesToYaml}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Convert to YAML
              </button>
            </div>

            {/* Error Message */}
            {error && <div className="p-4 bg-red-100 border border-red-200 text-red-700 rounded-md">{error}</div>}

            {/* Output */}
            <div>
              <div className="flex justify-between mb-2">
                <label htmlFor="yaml-output" className="text-sm font-medium">
                  YAML Output
                </label>
                <div className="space-x-2">
                  <button
                    className="text-sm text-blue-600 hover:text-blue-800"
                    onClick={() => copyToClipboard(yamlOutput)}
                    disabled={!yamlOutput}
                  >
                    Copy
                  </button>
                  <button
                    className="text-sm text-blue-600 hover:text-blue-800"
                    onClick={() => downloadFile(yamlOutput, "yaml")}
                    disabled={!yamlOutput}
                  >
                    Download
                  </button>
                </div>
              </div>
              <textarea
                id="yaml-output"
                placeholder="YAML output will appear here..."
                className="w-full h-64 p-2 border border-gray-300 rounded-md font-mono text-sm"
                value={yamlOutput}
                readOnly
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default YamlPropertiesConverter
